<template>
    <div class="w-full mx-auto px-4 pt-16 mt-10 py-20 bg-silver h-screen overflow-y-auto" ref="wrapperEl">
        <LoadingDialog v-if="loadingHappening" />
        <section class="w-full mx-3 md:w-1/2 mx-auto pt-10 md:pt-0">
            <button @click="restart()" class="mb-10 mt-5 md:mt-10 p-2 border shadow-lg rounded-lg"
                :class="interactionsAvaliable.typingMessage ? 'opacity-5' : ''"
                :disabled="interactionsAvaliable.typingMessage">
                {{ translationHandler.translate('restart', 'upper') }}
                <i class="pi pi pi-sync ml-2"></i>
            </button>
            <div v-for="(conversation, index) of conversationHistory" :key="index" class="w-full">
                <div class="flex mb-3">
                    <img class="w-14 h-14 mr-2 rounded-full" src="img/icon.webp">
                    <p class="p-3 rounded-r-lg shadow-lg border border-white bg-white text-void flex flex-col"
                        v-if="conversation.type != 'multiple-selection'">
                        {{ bindVariables(conversation) }}
                        <img v-if="conversation.type == 'image'" :src="conversation.img" style="width: 30rem!important; height: 30rem!important;">

                        <span v-if="isAwaitingInput(conversation) && !hasAnswer(conversation)"
                            :class="getQuestionClass(conversation, index)">
                            <template v-if="['email'].includes(conversation.rule)">
                                <InputText type="'email'" :unstyled="true" class="reset-css w-full"
                                    v-model="conversationData[conversation.attr]"
                                    @keyup.enter="sendAnswer(conversation, index)" />
                                <button @click="sendAnswer(conversation, index)">
                                    <i class="pi pi pi-send"></i>
                                </button>
                            </template>
                            <template v-if="conversation.rule == 'phone'">
                                <PhoneInput v-model="conversationData[conversation.attr]"  @selectedCountry="onCountryChange" @triggerEnter="sendAnswer(conversation, index)" />
                                <button @click="sendAnswer(conversation, index)" class="ml-2">
                                    <i class="pi pi pi-send"></i>
                                </button>
                            </template>
                            <template v-if="['name'].includes(conversation.rule)">
                                <InputText type="text" :unstyled="true" class="reset-css w-full"
                                    v-model="conversationData[conversation.attr]"
                                    @keyup.enter="sendAnswer(conversation, index)" />
                                <button @click="sendAnswer(conversation, index)">
                                    <i class="pi pi pi-send"></i>
                                </button>
                            </template>
                            <template v-if="['text-area'].includes(conversation.rule)">
                                <div class="w-full flex border rounded p-2">
                                    <Textarea 
                                        v-model="conversationData[conversation.attr]" 
                                        rows="5" 
                                        cols="30" 
                                        autoResize 
                                        style="width: 100% !important; border: unset !important;" 
                                        @input="setTextAreaSize(conversation)"
                                    />
                                    <button @click="sendAnswer(conversation, index)">
                                        <i class="pi pi pi-send"></i>
                                    </button>
                                </div>
                                <small>{{ textAreaSize }} {{ translationHandler.translate('of') }} 4000</small>
                            </template>
                            <template v-if="['', undefined, 'text'].includes(conversation.rule)">
                                <InputText type="text" :unstyled="true" class="reset-css w-full"
                                    v-model="conversationData[conversation.attr]"
                                    @keyup.enter="sendAnswer(conversation, index)" />
                                <button @click="sendAnswer(conversation, index)">
                                    <i class="pi pi pi-send"></i>
                                </button>
                            </template>
                            <template v-if="['url'].includes(conversation.rule)">
                                <div class="w-full flex border rounded p-2">
                                    <InputText type="text" :unstyled="true" class="reset-css w-full" placeholder="https://"
                                        v-model="conversationData[conversation.attr]"
                                        @keyup.enter="sendAnswer(conversation, index)" />
                                    <button @click="sendAnswer(conversation, index)">
                                        <i class="pi pi pi-send"></i>
                                    </button>
                                </div>
                                <small class="mt-2">
                                    {{ translationHandler.translate("Add 'https://' to your URL") }}
                                </small>
                            </template>
                        </span>

                        <span v-if="hasError(index)" class="text-candy text-lg">
                            {{ interactionsAvaliable.inputError?.message }}
                        </span>
                    </p>

                    <template v-if="conversation.type == 'multiple-selection'">
                        <div class="flex flex-col">
                            <p class="p-3 rounded-r-lg shadow-lg border border-white bg-white text-void flex mb-3">
                                {{ bindVariables(conversation) }}
                            </p>
                            <div class="w-auto" v-if="!hasAnswer(conversation)">
                                <p v-for="(opt, optIndex) of conversation.options" :key="optIndex"
                                    class="p-3 mb-3 rounded-r-lg shadow-lg border border-chain bg-chain text-silver flex flex-col cursor-pointer hover:bg-iron hover-border-iron hover:text-void"
                                    @click="selectOption(conversation, opt)">
                                    {{ translationHandler.translate(opt.text) }}
                                </p>
                            </div>
                        </div>
                    </template>

                </div>

                <div v-if="hasAnswer(conversation)" class="flex mb-3">
                    <p
                        class="p-3 rounded-l-lg shadow-lg border border-dark-silver bg-dark-silver text-white flex flex-col ml-auto">
                        {{ translationHandler.translate(conversationData[conversation.attr]) }}
                    </p>
                </div>
            </div>

            <div v-if="interactionsAvaliable.typingMessage">
                <TypingDotsComponent />
            </div>

            <div v-if="secondsToRedirect && secondsToRedirect > 0">
                {{ translationHandler.translate('You will be redirected in') }} {{ secondsToRedirect / 1000 }} {{ translationHandler.translate('seconds!') }}
            </div>
        </section>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue';
import thankYouImage from '@/assets/images/thank_you.webp';
import { sleep } from '@/composables/sleep';
import TypingDotsComponent from '@/components/Utils/TypingDotsComponent.vue';
import { InputText, useToast } from 'primevue';
import Textarea from 'primevue/textarea';
import translationHandler from '@/composables/handlers/TranslationHandler';
import requestHandler from '@/composables/requestHandler';
import PhoneInput from '@/components/Utils/PhoneComponent.vue';
import urlHandler from '@/composables/urlHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import Budget from '@/api/models/Budget';
import budgetService from '@/services/BudgetService';
import { showToast } from '@/composables/toast';
import DomHandler from '@/composables/handlers/DomHandler';
import SupportService from '@/services/SupportService';
import utils from '@/composables/utils';

const toast = useToast();
const loadingHappening = ref(true);
const htmlElement = ref();
onMounted(() => {
    disableScreenScroll();
    startConversation();

    loadingHappening.value = false;
});

function disableScreenScroll() {
    if (!htmlElement.value) {
        htmlElement.value = document.querySelector('html');
    }
    htmlElement.value.style.overflowY = 'hidden';
}

const interactionsAvaliable = reactive({
    index: 0,
    total: 19,
    typingMessage: false,
    awaitingInput: false,
    inputError: null,
    data: {
        1: { question: 'Before we start, I want to get to know you better.' },
        2: { question: 'What is your name?', type: 'question', attr: 'name', rule: 'name' },
        3: {
            question: 'Alright ${name}, what is the email where we can send our proposal?', attr: 'email', type: 'question', rule: 'email',
            bindParams: ['name']
        },
        4: {question: 'Last question about you. What is your phone number? Just in case we need to contact you regarding your quote', attr: 'phone_number', type: 'question', rule: 'phone'},
        5: {
            question: 'Well ${name}, which of the categories below do you fit into?', attr: 'category',
            bindParams: ['name'],
            type: 'multiple-selection', options: [
                { text: 'Personal demand, for individuals.', to: 6 },
                { text: 'Business demand, for companies.', to: 7 }
            ]
        },
        6: {
            question: 'What service do you need?', attr: 'service', type: 'multiple-selection', to: 9, options: [
                { text: "I want my first website, I've never had one." },
                { text: 'My website is outdated, I want to modernize it.' },
                { text: 'I need some adjustments/improvements on my website.' },
                { text: "I want to optimize my website's SEO." },
                { text: 'I want a website.' },
                { text: 'I want an e-commerce.' },
                { text: 'I want a custom web system.' },
                { text: 'I need an API integration.' },
                { text: "I'm looking for automation using a bot/robot." },
                { text: 'I need to extract data from a portal automatically and consistently.' },
            ]
        },
        7: {
            question: "What is your company name? You may leave it blank if you prefer.",
            attr: 'company_name', type: 'question', to: 19
        },
        19: {
            question: "Would you like to share your company's website?",
            attr: 'company_website', type: 'question', to: 8, rule: 'url'
        },
        8: {
            question: 'Perfect ${name}, about your company ${company_name}, you would say that:',
            bindParams: ['name', 'company_name'],
            attr: 'company_type', type: 'multiple-selection', to: 6, options: [
                { text: 'It is a new company, recently started or not yet operating' },
                { text: 'It is an established company, already in the market for some time with a digital presence' },
                { text: 'It is a long-standing company, but it has no digital presence' }
            ]
        },
        9: { question: "Okay, you're in the right place, we want to help you!", type: 'info' },
        10: {
            question: "First of all, I'd like to explain that our prices and quotes are 100% customized and adapt to your demand, in both complexity and requirements.",
            type: 'info'
        },
        11: { question: 'The more details, the better.', type: 'small-text' },
        12: {
            question: 'To generate a quote, please present your idea below:',
            attr: 'idea', type: 'question', rule: 'text-area'
        },
        13: {
            question: 'How soon do you need this service completed?',
            attr: 'delivery_time', type: 'multiple-selection', options: [
                { text: "It's urgent, I need the service within 7 days." },
                { text: 'I need the service within 15 days.' },
                { text: "It's not very urgent, we can work within 30 days." },
                { text: "It's still just an idea, so I'm not in a hurry. I want to focus on the details and follow the development." }
            ]
        },
        14: {
            question: 'Very well ${name}, thank you for the information and for your time.',
            type: 'info',
            bindParams: ['name']
        },
        15: {
            question: 'We now have enough information to start thinking about your quote.',
            type: 'info'
        },
        16: {
            question: "We'll get in touch as soon as possible via email or phone.",
            type: 'info'
        },
        17: {
            question: 'CervoDigital thanks you for getting in touch and wishes you an excellent day.',
            img: thankYouImage, type: 'image'
        },
        18: {
            question: 'You will be redirected in a few seconds.',
            type: 'small-text', action: 'finish', stop: true
        }
    }
});
let conversationHistory = ref([]);
let conversationData = reactive({
    sequence: []
});
function startConversation() {
    interactionsAvaliable.awaitingInput = false;
    interactionsAvaliable.typingMessage = false;
    interactionsAvaliable.index = 1;
    conversationHistory.value = [];
    conversationData = {
        sequence: []
    };
}

watch(
    () => interactionsAvaliable.index,
    () => {
        makeQuestion();
    }
)

const wrapperEl = ref(null);
async function scrollDown() {
    await sleep(100);
    if (wrapperEl.value) {
        wrapperEl.value.scrollTop = wrapperEl.value.scrollHeight;
    }
}

async function makeQuestion() {
    interactionsAvaliable.typingMessage = true;
    const typingTime = evaluateTypingTime();
    await sleep(typingTime * 1000);
    interactionsAvaliable.typingMessage = false;
    const conversation = getCurrentFlowItem();
    if (!conversation) {
        return;
    }

    if (!conversation.stop) {
        conversationHistory.value.push(conversation);
        if (!isAwaitingInput(conversation)) {
            interactionsAvaliable.index++;
        } else {
            awaitUserInput();
        }
    }

    if (conversation.action) {
        checkAction(conversation.action);
    }

    scrollDown();
}

async function awaitUserInput() {
    interactionsAvaliable.awaitingInput = true;
    scrollDown();
}

function checkAction(type = '') {
    switch (type) {
        case 'finish':
            finish();
            break;
    }
}

const secondsToRedirect = ref(null);
async function finish() {

    let objectData = {};
    for (let attr of conversationData.sequence) {
        objectData[attr] = conversationData[attr];
    }
    objectData['selectedCountry'] = conversationData['selectedCountry'];

    const budgetObject = new Budget(objectData);
    const res = await budgetService.saveBudget(budgetObject, false);
    if (!res) {
        SupportService.informSupportError({
            data: utils.copyVariable(budgetObject), 
            action: 'BudgetService.saveAdvancedBudget', 
            component: 'BudgetView'
        });
    }

    secondsToRedirect.value = 10000;
    while (secondsToRedirect.value > 0) {
        await sleep(1000);
        secondsToRedirect.value = secondsToRedirect.value - 1000;
    }

    requestHandler.redirect('/thankyou', {name: conversationData['name'] ?? ''});
}

function evaluateTypingTime() {
    const conversation = getCurrentFlowItem();
    if (!conversation) {
        return 1;
    }
    const length = conversation.question.length;
    return length / 20;
}

function getCurrentFlowItem() {
    return interactionsAvaliable.data[interactionsAvaliable.index] || null;
}

function isAwaitingInput(conversationItem = null) {
    const awaitInput = ['question', 'multiple-selection'];
    const conversation = conversationItem;
    return awaitInput.includes(conversation.type);
}

async function sendAnswer(conversation, index) {
    let data = {
        valid: false,
        message: 'error'
    };
    let value = conversationData[conversation.attr];

    let rawNumber = '';
    try {
        rawNumber = value.replace(/[^\d+]/g, '');
    } catch (error) {
        rawNumber = '';
    }

    switch (conversation.rule) {
        case 'email':
            data.valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            data.message = 'email is invalid';
            break;
        case 'phone':
            data.valid = /^\+?\d{10,15}$/.test(rawNumber.replace(/\s+/g, ''));
            data.message = 'telephone number is invalid';
            break;
        case 'text':
            data.valid = value.length > 20;
            data.message = 'text is invalid';
            break;
        case 'text-area':
            data.valid = value.length > 50;
            data.message = 'tell us more about your idea, 50 characters at least';
            break;
        case 'name':
            data.valid = value.split(' ').length == 1 || value.length < 6 ? false : true;
            data.message = 'your full name is required';
            break;
        case 'url':
            if (value && value.length > 0){
                loadingHappening.value = true;
                data.valid = await urlHandler.isUrlValid(value);
                loadingHappening.value = false;
                data.message = !data.valid ? 'invalid url' : '';
            } else {
                data.valid = true;
            }
            break;
        default:
            data.valid = true;
            break;

    }

    const optionalValue = ['company_name', 'company_website'];
    if (optionalValue.includes(conversation.attr) && ['',null,undefined].includes(value)) {
        conversationData[conversation.attr] = '...';
    }

    if (!data.valid) {
        interactionsAvaliable.inputError = {
            message: translationHandler.translate(data.message, 'ucfirst'),
            index: index
        }
        return false;
    }

    interactionsAvaliable.inputError = null;

    conversationData[`has-${conversation.attr}`] = true;
    conversationData['sequence'].push(conversation.attr);

    if (conversation.to) {
        interactionsAvaliable.index = conversation.to;
    } else {
        interactionsAvaliable.index++;
    }
}

function hasAnswer(conversation) {
    return !['', undefined, null].includes(conversationData[`has-${conversation.attr}`]);
}

function bindVariables(conversation) {
    let question = translationHandler.translate(conversation.question);

    let questionArr = question.split('${');
    let parsedQuestion = '';
    for (let index = 0; index < questionArr.length; index++) {
        let textVal = questionArr[index];
        if (index == 0) {
            parsedQuestion = textVal;
        } else if (textVal.indexOf('}')) {
            const textArr = textVal.split('}');
            let variable = textArr[0];
            parsedQuestion += conversationData[variable] + textArr[1];
        } else {
            parsedQuestion += textVal;
        }
    }

    return parsedQuestion;
}

function restart() {
    startConversation();
}

function hasError(index) {
    if (!interactionsAvaliable.inputError || interactionsAvaliable.inputError.index != index) {
        return false;
    }
    return interactionsAvaliable.inputError?.message ?? false;
}

function selectOption(conversation, option) {
    if (!conversation || !option) {
        return;
    }

    conversationData[`has-${conversation.attr}`] = true;
    conversationData['sequence'].push(conversation.attr);
    conversationData[conversation.attr] = option.text;


    const correctIndexToGo = conversation.to ?? option.to;

    if (correctIndexToGo) {
        interactionsAvaliable.index = correctIndexToGo;
    } else {
        interactionsAvaliable.index++;
    }
}

function onCountryChange(country) {
    if (country.code) {
        conversationData['selectedCountry'] = country.code;
    }
}

function getQuestionClass(conversation = null, index = 0) {
    if (conversation) {
        let classStyle = hasError(index) ? 'border-candy ' : '';
        switch (conversation.rule) {
            case 'phone':
                classStyle = 'flex mt-5';
            break;
            case 'url':
                classStyle = 'flex flex-col mt-5';
            break;
            case 'text-area':
                classStyle = 'flex flex-col mt-5';
            break;
            default:
                classStyle = 'flex my-3 border rounded p-2'
            break;
        }
        return classStyle;
    }
    return '';
}

const lastTextAreaValue = ref('');
const textAreaSize = ref(0);
function setTextAreaSize(conversation = null) {
    if (conversation) {
        let value = conversationData[conversation.attr];
        if (!value) {
            textAreaSize.value = 0;
            lastTextAreaValue.value = '';
            return;
        }

        if (value.length > 4000) {
            conversationData[conversation.attr] = value.slice(0, 4000);
            showToast(toast, 'Limite máximo de 4000 caracteres atingido.', 'info');
        }

        lastTextAreaValue.value = conversationData[conversation.attr];
        textAreaSize.value = lastTextAreaValue.value.length;
    }
    DomHandler.scrollDown();
}
</script>